import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Users,
  CreditCard,
  TrendingUp,
  ShieldCheck,
  AlertTriangle,
  Activity,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

function StatCard({ label, value, sub, icon: Icon, accent = "indigo", testid }) {
  const ring = {
    indigo: "ring-indigo-200 bg-indigo-50 text-indigo-700",
    sky: "ring-sky-200 bg-sky-50 text-sky-700",
    emerald: "ring-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "ring-amber-200 bg-amber-50 text-amber-700",
    rose: "ring-rose-200 bg-rose-50 text-rose-700",
    zinc: "ring-zinc-200 bg-zinc-50 text-zinc-700",
  }[accent];
  return (
    <div data-testid={testid} className="rounded-xl bg-white border border-[#E8DDC9] p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
          <div className="mt-2 text-2xl font-display font-black text-zinc-900">{value}</div>
          {sub ? <div className="mt-1 text-xs text-zinc-500">{sub}</div> : null}
        </div>
        <div className={`h-9 w-9 rounded-lg ring-1 flex items-center justify-center ${ring}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-xl bg-white border border-[#E8DDC9] p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-black text-lg">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function inr(n) {
  if (n == null) return "—";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [payments, setPayments] = useState({ items: [], total: 0 });
  const [subs, setSubs] = useState({ items: [], total: 0 });
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const [m, u, p, s] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/users", { params: { limit: 25, q: q || undefined } }),
        api.get("/admin/payments", { params: { limit: 25 } }),
        api.get("/admin/subscriptions", { params: { limit: 25, status: "active" } }),
      ]);
      setMetrics(m.data);
      setUsers(u.data);
      setPayments(p.data);
      setSubs(s.data);
    } catch (e) {
      apiErrorToast(e, "Failed to load admin metrics");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user?.is_admin) load();
  }, [authLoading, user?.is_admin]);

  if (authLoading) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="p-8" data-testid="admin-denied">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 max-w-xl">
          <div className="flex items-center gap-2 text-rose-700 font-display font-black text-lg">
            <ShieldCheck size={18} /> Admin access required
          </div>
          <p className="text-rose-700/80 text-sm mt-2">
            Your account is not flagged as admin. If this is unexpected, ask the platform owner
            to add your email to <code className="font-mono text-xs bg-white/60 px-1 rounded">ADMIN_EMAILS</code>.
          </p>
        </div>
      </div>
    );
  }

  const m = metrics;
  const dailyMax = Math.max(1, ...((m?.revenue?.daily_14d || []).map((d) => d.list || 0)));

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Internal</div>
          <h1 className="font-display font-black text-3xl md:text-4xl">Admin Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Snapshot of users, payments and subscriptions.
          </p>
        </div>
        <button
          data-testid="admin-refresh"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-300 bg-white hover:border-zinc-900 transition text-sm font-medium disabled:opacity-50"
        >
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          testid="metric-total-users"
          label="Total Registrations"
          value={m?.users?.total ?? "—"}
          sub={m ? `+${m.users.new_7d} in last 7d` : ""}
          icon={Users}
          accent="indigo"
        />
        <StatCard
          testid="metric-trial-users"
          label="Trial Users (Free)"
          value={m?.users?.trial ?? "—"}
          sub={m ? `${m.users.paid} on paid plans` : ""}
          icon={Activity}
          accent="sky"
        />
        <StatCard
          testid="metric-active-subs"
          label="Active Subscriptions"
          value={m?.subscriptions?.active ?? "—"}
          sub={m ? `${m.subscriptions.expired} expired` : ""}
          icon={ShieldCheck}
          accent="emerald"
        />
        <StatCard
          testid="metric-revenue"
          label="Revenue"
          value={inr(m?.revenue?.charged_total)}
          sub=""
          icon={TrendingUp}
          accent="amber"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          testid="metric-payments-paid"
          label="Payments — Paid"
          value={m?.payments?.paid ?? "—"}
          sub={m ? `${m.payments.paid_7d} in last 7d` : ""}
          icon={CreditCard}
          accent="emerald"
        />
        <StatCard
          testid="metric-payments-failed"
          label="Payments — Failed"
          value={m?.payments?.failed ?? "—"}
          sub={m ? `${m.payments.initiated} initiated total` : ""}
          icon={AlertTriangle}
          accent="rose"
        />
        <StatCard
          testid="metric-renewals"
          label="Renewals / Upgrades"
          value={m?.subscriptions?.renewals ?? "—"}
          icon={RefreshCw}
          accent="sky"
        />
        <StatCard
          testid="metric-activity"
          label="Resumes / Interviews"
          value={`${m?.activity?.resumes ?? 0} / ${m?.activity?.interviews ?? 0}`}
          icon={Activity}
          accent="zinc"
        />
      </div>

      {/* Revenue chart + by-plan */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Section title="Revenue — last 14 days">
            <div className="flex items-end gap-1 h-40" data-testid="revenue-chart">
              {(m?.revenue?.daily_14d || []).length === 0 ? (
                <div className="text-sm text-zinc-500">No paid transactions yet.</div>
              ) : (
                (m.revenue.daily_14d).map((d, idx) => {
                  const v = d.charged;
                  const h = Math.max(4, Math.round((v / dailyMax) * 140));
                  return (
                    <div key={d.day || `day-${idx}`} className="flex-1 flex flex-col items-center gap-1">
                      <div className="text-[9px] text-zinc-500">{inr(v)}</div>
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-indigo-500 to-sky-400"
                        style={{ height: `${h}px` }}
                        title={`${d.day}: ${inr(v)} (${d.count} txn)`}
                      />
                      <div className="text-[9px] text-zinc-400">{d.day.slice(5)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </Section>
        </div>
        <Section title="Revenue by plan">
          <div className="space-y-3" data-testid="revenue-by-plan">
            {(m?.revenue?.by_plan || []).length === 0 ? (
              <div className="text-sm text-zinc-500">No data.</div>
            ) : (
              m.revenue.by_plan.map((r, idx) => (
                <div key={r.plan_id || `plan-${idx}`} className="flex items-center justify-between text-sm">
                  <div className="capitalize font-medium text-zinc-800">{r.plan_id}</div>
                  <div className="text-right">
                    <div className="font-semibold">{inr(r.charged)}</div>
                    <div className="text-[11px] text-zinc-500">{r.count} txn</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Section>
      </div>

      {/* Users table */}
      <Section
        title={`Recent Users (${users.total})`}
        right={
          <div className="flex items-center gap-2">
            <input
              data-testid="admin-user-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search email or name…"
              className="px-3 py-1.5 text-sm rounded border border-zinc-300 focus:outline-none focus:border-zinc-900"
            />
            <button onClick={load} className="text-sm font-medium px-3 py-1.5 rounded border border-zinc-300 hover:border-zinc-900">
              Search
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3">Readiness</th>
                <th className="py-2 pr-3">Streak</th>
                <th className="py-2 pr-3">Joined</th>
                <th className="py-2 pr-3">Admin</th>
              </tr>
            </thead>
            <tbody data-testid="admin-users-table">
              {users.items.map((u, idx) => (
                <tr key={u.id || `u-${idx}`} className="border-b border-zinc-100">
                  <td className="py-2 pr-3 font-medium">{u.name || "—"}</td>
                  <td className="py-2 pr-3 text-zinc-600">{u.email}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${u.plan === "free" ? "bg-zinc-100 text-zinc-700" : "bg-emerald-100 text-emerald-800"}`}>
                      {u.plan || "free"}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{u.readiness_score ?? 0}</td>
                  <td className="py-2 pr-3">{u.current_streak ?? 0}</td>
                  <td className="py-2 pr-3 text-zinc-500">{fmtDate(u.created_at)}</td>
                  <td className="py-2 pr-3">{u.is_admin ? "✓" : ""}</td>
                </tr>
              ))}
              {users.items.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-zinc-500">No users.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Payments + Active subs (side-by-side on lg) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title={`Recent Payments (${payments.total})`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">When</th>
                </tr>
              </thead>
              <tbody data-testid="admin-payments-table">
                {payments.items.map((p, idx) => (
                  <tr key={p.order_id || `p-${idx}`} className="border-b border-zinc-100">
                    <td className="py-2 pr-3 text-zinc-700">{p.user_email || p.user_id?.slice(0, 8)}</td>
                    <td className="py-2 pr-3 capitalize">{p.plan_name || p.plan_id}</td>
                    <td className="py-2 pr-3">
                      {inr(p.amount)}
                      {p.test_mode ? <span className="ml-1 text-[10px] text-zinc-400" title="Historical test-mode payment">legacy</span> : null}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                          p.payment_status === "paid"
                            ? "bg-emerald-100 text-emerald-800"
                            : p.payment_status === "failed"
                            ? "bg-rose-100 text-rose-800"
                            : "bg-zinc-100 text-zinc-700"
                        }`}
                      >
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-zinc-500">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
                {payments.items.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-zinc-500">No payments yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={`Active Subscriptions (${subs.total})`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Activated</th>
                  <th className="py-2 pr-3">Expires</th>
                </tr>
              </thead>
              <tbody data-testid="admin-subs-table">
                {subs.items.map((s, idx) => (
                  <tr key={s.id || `s-${idx}`} className="border-b border-zinc-100">
                    <td className="py-2 pr-3 text-zinc-700">{s.customer_email || s.user_id?.slice(0, 8)}</td>
                    <td className="py-2 pr-3 capitalize">{s.plan_name || s.plan_id}</td>
                    <td className="py-2 pr-3 text-zinc-500">{fmtDate(s.activation_date)}</td>
                    <td className="py-2 pr-3 text-zinc-500">{fmtDate(s.expiry_date)}</td>
                  </tr>
                ))}
                {subs.items.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-zinc-500">No active subscriptions.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
